import React, { useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';

// Models
import { IGroupedRow, IGroupedRowItem } from '../groupPendingItems';
import { ICreateAnswerForm } from '../../interfaceCreateAnswer';
// Utils
import { getTranslateByKey } from '@/main/services/utils';
import { formatResponseToForm } from '../../utilscreateAnswer';

// Components
import SelectMultipleOutlined, { ISelectMultipleOption } from '@/main/components/shared/selectMultipleOutlined/selectMultipleOutlined';
import TableRowAnswer from './TableRowAnswer';
import {
  QuoteHeaderAnswerInformationResponse,
  QuoteSupplierItemPartResponse
} from '@/main/models/response';

interface ITableRowAnswerGroupedProps {
  group: IGroupedRow;
  fieldIds: (string | undefined)[];
  hasReadOnly: boolean;
  dataHeader?: QuoteHeaderAnswerInformationResponse;
  isFinishedTime?: boolean;
  openModalOnce: boolean;
  havePartInventory: boolean;
  setOpenModalOnce: React.Dispatch<React.SetStateAction<boolean>>;
  handleOpenModalDetails: (guidPart: string) => void;
  setAllSelected: React.Dispatch<React.SetStateAction<boolean>>;
  allSelected: boolean;
  setHavePartInventory: React.Dispatch<React.SetStateAction<boolean>>;
  isShowModalReasonAll: boolean;
  setIsShowModalReasonAll: React.Dispatch<React.SetStateAction<boolean>>;
  handleOpenModalImages: (item: QuoteSupplierItemPartResponse, allowEdit: boolean) => void;
}

interface IOrderedRow extends IGroupedRowItem {
  groupIdx: number;
}

function TableRowAnswerGrouped({
  group,
  fieldIds,
  hasReadOnly,
  dataHeader,
  isFinishedTime,
  openModalOnce,
  havePartInventory,
  setOpenModalOnce,
  handleOpenModalDetails,
  setAllSelected,
  allSelected,
  setHavePartInventory,
  isShowModalReasonAll,
  setIsShowModalReasonAll,
  handleOpenModalImages
}: ITableRowAnswerGroupedProps) {
  const { setValue } = useFormContext<ICreateAnswerForm>();
  const [selectedOptions, setSelectedOptions] = useState<ISelectMultipleOption[]>([]);

  const isGrouped = group.rows.length > 1;

  const formFieldNames = group.rows.map(
    ({ originalIndex }) => `pendingItems.${originalIndex}.isGroupHidden` as const
  );

  useWatch({ name: formFieldNames });

  useEffect(() => {
    if (!isGrouped) return;
    group.rows.forEach(({ originalIndex }) => {
      setValue(`pendingItems.${originalIndex}.isGroupHidden`, true);
    });
  }, []);

  function handlePartTypesChange(selected: ISelectMultipleOption[]) {
    const selectedKeys = new Set(selected.map(s => s.key));
    const previousKeys = new Set(selectedOptions.map(s => s.key));

    group.rows.forEach(({ item, originalIndex }) => {
      const isHidden = !selectedKeys.has(item.idPartType);
      setValue(`pendingItems.${originalIndex}.isGroupHidden`, isHidden);

      const wasVisible = previousKeys.has(item.idPartType);
      if (wasVisible && isHidden) {
        const resetValues = formatResponseToForm(item, false);
        setValue(`pendingItems.${originalIndex}`, { ...resetValues, isGroupHidden: true });
      }
    });

    setSelectedOptions(selected);
  }

  function handleRemovePartType(keyToRemove: string | number) {
    const newSelected = selectedOptions.filter(s => s.key !== keyToRemove);
    handlePartTypesChange(newSelected);
  }

  const commonProps = {
    fieldArrayName: 'pendingItems' as const,
    hasReadOnly,
    dataHeader,
    isFinishedTime,
    openModalOnce,
    havePartInventory,
    setOpenModalOnce,
    handleOpenModalDetails,
    setAllSelected,
    allSelected,
    setHavePartInventory,
    isShowModalReasonAll,
    setIsShowModalReasonAll,
    handleOpenModalImages
  };

  if (!isGrouped) {
    const { item, originalIndex } = group.rows[0];
    return (
      <TableRowAnswer
        key={fieldIds[0] ?? item.idUnique}
        row={item}
        rowIndex={originalIndex}
        {...commonProps}
      />
    );
  }

  const selectedKeys = new Set(selectedOptions.map(s => s.key));
  const visibleCount = selectedOptions.length;

  const orderedRows: IOrderedRow[] = useMemo(() => {
    const rowsWithIdx = group.rows.map((row, idx) => ({ ...row, groupIdx: idx }));

    if (selectedOptions.length === 0) return rowsWithIdx;

    const used = new Set<number>();
    const result: IOrderedRow[] = [];

    for (const opt of selectedOptions) {
      const found = rowsWithIdx.find(r => r.item.idPartType === opt.key);
      if (found && !used.has(found.groupIdx)) {
        result.push(found);
        used.add(found.groupIdx);
      }
    }

    for (const row of rowsWithIdx) {
      if (!used.has(row.groupIdx)) {
        result.push(row);
      }
    }

    return result;
  }, [group.rows, selectedOptions]);

  const allPartTypeOptions: ISelectMultipleOption[] = group.partTypeOptions.map(pt => ({
    key: pt.idPartType,
    value: pt.partTypeName
  }));

  const selectContent = (
    <SelectMultipleOutlined
      id={`id-grouped-parttype-${group.groupKey}`}
      options={allPartTypeOptions}
      selected={selectedOptions}
      onChange={handlePartTypesChange}
      onClearFirst={() => {
        if (selectedOptions.length > 0) {
          handleRemovePartType(selectedOptions[0].key);
        }
      }}
      placeholder={getTranslateByKey('information.partTypes')}
    />
  );

  return (
    <>
      {orderedRows.map(({ item, originalIndex, groupIdx }, renderIndex) => {
        const isSelected = selectedKeys.has(item.idPartType);
        const isFirstRow = renderIndex === 0;
        const isHidden = !isFirstRow && !isSelected;

        let partTypeContent: React.ReactNode | undefined;
        if (isFirstRow) {
          partTypeContent = selectContent;
        } else if (isSelected) {
          partTypeContent = (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>{item.partTypeName}</span>
              <IconButton
                size="small"
                onClick={() => handleRemovePartType(item.idPartType)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          );
        }

        return (
          <TableRowAnswer
            key={fieldIds[groupIdx] ?? item.idUnique}
            row={item}
            rowIndex={originalIndex}
            {...commonProps}
            hidden={isHidden}
            hideGroupedCells={!isFirstRow && isSelected}
            groupedRowSpan={isFirstRow && visibleCount > 1 ? visibleCount : undefined}
            partTypeContent={partTypeContent}
            disabledGroupFields={isFirstRow && visibleCount === 0}            
          />
        );
      })}
    </>
  );
}

export default TableRowAnswerGrouped;
